import { Box, Button, Stack, Typography, useTheme } from "@mui/material";
import { type FC, useMemo } from "react";
import { ReminderEntityTypeEnum } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { PaperworkStepLayout } from "web_myplann/Profile/Components/Paperwork/PaperworkStepLayout";
import type { PaperworkDocType } from "web_myplann/Profile/Constants/paperworkDocTypes";
import type { usePaperworkDocumentForm } from "web_myplann/Profile/Hooks/usePaperworkDocumentForm";
import { ReminderDraftSection } from "web_myplann/Reminders/Components/ReminderDraftSection";
import { RemindersSection } from "web_myplann/Reminders/Components/RemindersSection";
import type { ReminderEntity } from "web_myplann/Reminders/Types/ReminderTypes";
import { FlatRowFormDatePicker } from "web_myplann/Shared/Components/FlatRow/FlatRowFormDatePicker";
import { FlatRowFormInput } from "web_myplann/Shared/Components/FlatRow/FlatRowFormInput";
import { FlatRowGroup } from "web_myplann/Shared/Components/FlatRow/FlatRowGroup";
import { FlatRowInput } from "web_myplann/Shared/Components/FlatRow/FlatRowInput";
import { FlatRowSwitch } from "web_myplann/Shared/Components/FlatRow/FlatRowSwitch";
import { DateView } from "web_myplann/Shared/Types/DateView";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";

const R = Resources.Paperwork.Upload;

type Props = {
	type: PaperworkDocType;
	typeName: string;
	form: ReturnType<typeof usePaperworkDocumentForm>;
	isUpdate?: boolean;
	onSubmit: (result: { reminderSet: boolean }) => void;
	onBack: () => void;
};

export const PaperworkFormStep: FC<Props> = ({
	type,
	typeName,
	form,
	isUpdate = false,
	onSubmit,
	onBack,
}) => {
	const { t } = useResource();
	const { spacingTokens } = useTheme();
	const {
		control,
		noExpiry,
		setNoExpiry,
		validTo,
		documentId,
		isRemindersEnabled,
		reminderDrafts,
		setReminderDrafts,
		persistedReminderIds,
		isSubmitting,
		submit,
	} = form;

	const reminderEntity = useMemo<ReminderEntity | null>(
		() =>
			isUpdate && documentId !== undefined
				? {
						type: ReminderEntityTypeEnum.IdentificationDocument,
						id: documentId,
						validTo,
					}
				: null,
		[isUpdate, documentId, validTo],
	);

	const showReminders = isRemindersEnabled && !noExpiry && validTo != null;

	return (
		<PaperworkStepLayout title={typeName} onBack={onBack}>
			<Stack py={spacingTokens.xl} gap={spacingTokens.xl}>
				<Typography variant="subtitle3" px={spacingTokens.lg}>
					{t(R.Form.sectionTitle)}
				</Typography>

				<FlatRowGroup>
					{type.isCustom ? (
						<FlatRowFormInput
							control={control}
							name="customName"
							label={t(R.Form.customName)}
							placeholder={t(R.Form.customNamePlaceholder)}
							isLast={noExpiry}
						/>
					) : (
						<>
							<FlatRowInput label={t(R.Form.type)} value={typeName} readOnly />
							<FlatRowFormInput
								control={control}
								name="number"
								label={t(R.Form.number)}
								isLast={noExpiry}
							/>
						</>
					)}
					{!noExpiry && (
						<FlatRowFormDatePicker
							control={control}
							name="validTo"
							label={t(R.Form.validTo)}
							views={[DateView.Year, DateView.Month, DateView.Day]}
							displayFormat={DateFormat.DisplaySpaced}
							placeholder={t(R.Form.selectDate)}
							isLast
						/>
					)}
				</FlatRowGroup>

				<FlatRowGroup>
					<FlatRowSwitch
						label={t(R.Form.noExpiry)}
						checked={noExpiry}
						onChange={setNoExpiry}
						isLast
					/>
				</FlatRowGroup>

				{showReminders && (
					<Box px={spacingTokens.lg}>
						{reminderEntity ? (
							<RemindersSection entity={reminderEntity} />
						) : (
							<ReminderDraftSection
								drafts={reminderDrafts}
								onChange={setReminderDrafts}
								persistedIds={persistedReminderIds}
								validTo={validTo}
							/>
						)}
					</Box>
				)}

				<Box px={spacingTokens.lg}>
					<Button
						variant="primary"
						fullWidth
						disabled={isSubmitting}
						onClick={() => submit(onSubmit)}
					>
						{t(R.Form.submit)}
					</Button>
				</Box>
			</Stack>
		</PaperworkStepLayout>
	);
};
